/**
 * Sky Direction & Objects Visualization
 *
 * Creates an interactive sky chart showing:
 * - Search cone (azimuth/altitude area searched)
 * - Found objects positioned on the sky map
 * - Cardinal directions and horizon reference
 * - Interactive object selection
 */

export class SkyVisualization {
    constructor(calculator = null) {
        this.containerId = 'skyDirectionPlot';
        this.plotDiv = null;
        this.currentData = null;
        this.searchParams = null;
        this.calculator = calculator; // For coordinate conversions
        this.objectPaths = null; // Store calculated paths

        // Dark theme colors matching the Obsidian palette
        this.darkTheme = {
            paper_bgcolor: '#0a0a0a',
            plot_bgcolor: '#141414',
            text_primary: '#ffffff',
            text_secondary: '#b0b0b0',
            text_muted: '#707070',
            accent: '#6b8cae',
            border: '#2a2a2a',
            horizon: 'rgba(255, 165, 0, 0.6)',
            sphere: 'rgba(107, 140, 174, 0.1)',
            searchArea: 'rgba(107, 140, 174, 0.3)',
            searchBorder: 'rgba(107, 140, 174, 0.8)'
        };
    }

    /**
     * Initialize the sky visualization
     */
    initialize() {
        console.log('Initializing SkyVisualization...');

        this.plotDiv = document.getElementById(this.containerId);
        if (!this.plotDiv) {
            console.log('Sky visualization container not found - sky visualization disabled');
            return false;
        }

        // Check if Plotly is available
        if (typeof Plotly === 'undefined') {
            console.warn('Plotly library not found - sky visualization disabled');
            return false;
        }

        console.log('SkyVisualization initialized successfully');
        return true;
    }

    /**
     * Create sky chart with search results
     * @param {Array} objects - Found objects with coordinates
     * @param {Object} searchParams - Search parameters (azimuth, altitude, radius, etc.)
     */
    async createSkyChart(objects, searchParams) {
        console.log('createSkyChart called with', objects?.length, 'objects');

        if (!objects || objects.length === 0) {
            this.showEmptySkyChart();
            return;
        }

        // Calculate object paths if we have a calculator
        if (this.calculator && objects.length > 0) {
            this.objectPaths = await this.calculateObjectPaths(objects, searchParams);
        }

        this.currentData = { objects, searchParams };
        this.searchParams = searchParams;

        await this.renderSkyChart();
    }

    /**
     * Render the sky chart
     */
    async renderSkyChart(retryCount = 0) {
        console.log('renderSkyChart called');
        const { objects, searchParams } = this.currentData;

        // Check if container is visible and has dimensions
        if ((!this.plotDiv.offsetWidth || !this.plotDiv.offsetHeight) && retryCount < 10) {
            console.log(`Sky chart container not visible yet (${this.plotDiv.offsetWidth}x${this.plotDiv.offsetHeight}), retry ${retryCount + 1}/10`);
            setTimeout(() => this.renderSkyChart(retryCount + 1), 200);
            return;
        }

        // If still not visible after retries, force minimum dimensions
        if (!this.plotDiv.offsetWidth || !this.plotDiv.offsetHeight) {
            console.log('Sky chart container still not visible, forcing dimensions...');
            this.plotDiv.style.minHeight = '400px';
            this.plotDiv.style.minWidth = '100%';
            // Wait a bit for the style to apply
            setTimeout(() => this.renderSkyChart(retryCount + 1), 100);
            if (retryCount > 15) {
                console.error('Failed to make sky chart container visible after maximum retries');
                return;
            }
        }

        // Prepare sky chart data
        const skyData = this.prepareSkyData(objects, searchParams);
        console.log('Sky data prepared:', skyData.length, 'traces');

        // Create layout
        const layout = this.createSkyLayout(searchParams);

        // Hide loading placeholder
        const loadingPlaceholder = document.getElementById('skyPlotLoading');
        if (loadingPlaceholder) {
            loadingPlaceholder.style.display = 'none';
        }

        // Get container dimensions for square sizing (to prevent egg-shape)
        const containerRect = this.plotDiv.getBoundingClientRect();
        const containerWidth = containerRect.width || this.plotDiv.offsetWidth || 800;

        // Calculate square size to prevent egg-shaped hemisphere
        const squareSize = this.calculateSquareSize(containerWidth);

        // Force square dimensions - aspectratio in scene will handle hemisphere proportions
        const responsiveLayout = { ...layout };
        responsiveLayout.autosize = false;
        responsiveLayout.width = squareSize;
        responsiveLayout.height = squareSize;

        // Render with Plotly
        Plotly.newPlot(this.plotDiv, skyData, responsiveLayout, {
            responsive: false, // Disable responsive to maintain centering
            displayModeBar: true,
            modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
            displaylogo: false
        }).then(() => {
            this.adjustSceneDomain();
            if (!this._resizeHandler) {
                this._resizeHandler = () => this.adjustSceneDomain();
                window.addEventListener('resize', this._resizeHandler);
            }
            // Force a resize after initial render to maintain square shape and centering
            setTimeout(() => {
                const redrawWidth = this.plotDiv.getBoundingClientRect().width || this.plotDiv.offsetWidth;
                const newSize = this.calculateSquareSize(redrawWidth);
                Plotly.relayout(this.containerId, {
                    width: newSize,
                    height: newSize,
                    autosize: false,
                    margin: { l: 20, r: 20, t: 60, b: 30 }
                }).then(() => {
                    this.adjustSceneDomain();
                    console.log('Sky chart resized to square dimensions with centering');
                    // Force another layout update for proper centering
                    setTimeout(() => {
                        Plotly.Plots.resize(this.plotDiv);
                        this.adjustSceneDomain();
                    }, 50);
                }).catch(() => {
                    Plotly.Plots.resize(this.plotDiv);
                    this.adjustSceneDomain();
                    console.log('Sky chart resized (fallback)');
                });
            }, 200); // Increased timeout for better DOM settling
        });

        // Add click event handler
        this.plotDiv.on('plotly_click', (data) => {
            this.handleSkyClick(data);
        });
    }

    /**
     * Keep the 3D scene centered and circular by letterboxing the scene domain
     */
    adjustSceneDomain() {
        if (!this.plotDiv || typeof Plotly === 'undefined') {
            return;
        }

        const rect = this.plotDiv.getBoundingClientRect();
        const width = Math.max(rect.width || this.plotDiv.offsetWidth || 0, 1);
        const height = Math.max(rect.height || this.plotDiv.offsetHeight || 0, 1);

        let x0 = 0;
        let x1 = 1;
        let y0 = 0;
        let y1 = 1;

        if (width > height) {
            const span = height / width;
            x0 = (1 - span) / 2;
            x1 = x0 + span;
        } else if (height > width) {
            const span = width / height;
            y0 = (1 - span) / 2;
            y1 = y0 + span;
        }

        Plotly.relayout(this.plotDiv, {
            'scene.domain.x': [x0, x1],
            'scene.domain.y': [y0, y1]
        }).catch(() => {});
    }

    /**
     * Calculate object paths throughout the night
     * @param {Array} objects - Objects with RA/Dec coordinates
     * @param {Object} searchParams - Search parameters with location and date
     * @returns {Object} Object paths data
     */
    async calculateObjectPaths(objects, searchParams) {
        // For now, create simulated paths based on visibility data
        // This will be enhanced when proper coordinate conversion is available
        const paths = {};

        // Create simulated paths for top objects
        for (const obj of objects.slice(0, 10)) { // Limit to top 10 for performance
            if (!obj.visibility) continue;

            const objPath = {
                name: obj.name,
                x: [],
                y: [],
                z: [],
                altitudes: [],
                azimuths: []
            };

            // Create a simplified path arc based on visibility data
            const numPoints = 20;
            const startAz = obj.visibility.riseAzimuth || searchParams.azimuth - 30;
            const endAz = obj.visibility.setAzimuth || searchParams.azimuth + 30;
            const peakAlt = obj.visibility.peakAltitude || searchParams.altitude;

            for (let i = 0; i <= numPoints; i++) {
                const fraction = i / numPoints;

                // Create arc path
                const az = startAz + (endAz - startAz) * fraction;
                // Parabolic altitude curve
                const alt = peakAlt * (1 - Math.pow(2 * fraction - 1, 2));

                if (alt > 0) {
                    const coords = this.azAltToSphereCoords(az, alt, 1.01);
                    objPath.x.push(coords.x);
                    objPath.y.push(coords.y);
                    objPath.z.push(coords.z);
                    objPath.altitudes.push(alt);
                    objPath.azimuths.push(az);
                } else {
                    objPath.x.push(null);
                    objPath.y.push(null);
                    objPath.z.push(null);
                    objPath.altitudes.push(null);
                    objPath.azimuths.push(null);
                }
            }

            paths[obj.name] = objPath;
        }

        return paths;
    }

    /**
     * Prepare sky chart data traces for 3D sphere
     * @param {Array} objects - Objects to display
     * @param {Object} searchParams - Search parameters
     * @returns {Array} Plotly data traces
     */
    prepareSkyData(objects, searchParams) {
        const traces = [];

        // Add 3D celestial sphere (surface)
        const sphereTraces = this.createCelestialSphere();
        traces.push(...sphereTraces);

        // Add horizon circle on the sphere
        const horizonTrace = this.createHorizonCircle3D();
        traces.push(horizonTrace);

        // Add cardinal direction markers
        const cardinalTrace = this.createCardinalDirections3D();
        traces.push(cardinalTrace);

        // Add search cone visualization on sphere (improved)
        const searchConeTrace = this.createSearchCone3D(searchParams);
        if (searchConeTrace) traces.push(...searchConeTrace); // Note: now returns array

        // Add object paths if available
        if (this.objectPaths) {
            const pathTraces = this.createObjectPaths3D(objects);
            traces.push(...pathTraces);
        }

        return traces;
    }

    /**
     * Create 3D search cone visualization on sphere surface
     * @param {Object} searchParams - Search parameters
     * @returns {Array} Array of Plotly traces for the search area
     */
    createSearchCone3D(searchParams) {
        const { azimuth, altitude, minAltitude = altitude - 15, maxAltitude = altitude + 15, fieldOfView = 15 } = searchParams;
        const searchRadius = fieldOfView || 15;
        const traces = [];

        // Create a mesh to fill the search area on the sphere
        const meshPoints = { x: [], y: [], z: [], i: [], j: [], k: [] };
        const steps = 20; // Resolution of the mesh

        // Generate mesh grid for the search area
        const points = [];
        for (let altStep = 0; altStep <= steps; altStep++) {
            for (let azStep = 0; azStep <= steps; azStep++) {
                const altFrac = altStep / steps;
                const azFrac = azStep / steps;

                // Calculate position within search area
                const currentAlt = minAltitude + (maxAltitude - minAltitude) * altFrac;
                const currentAz = azimuth - searchRadius + (2 * searchRadius * azFrac);

                // Keep altitude within bounds
                const clampedAlt = Math.max(0, Math.min(90, currentAlt));
                const normalizedAz = ((currentAz % 360) + 360) % 360;

                const coords = this.azAltToSphereCoords(normalizedAz, clampedAlt, 1.005); // Just above sphere surface
                meshPoints.x.push(coords.x);
                meshPoints.y.push(coords.y);
                meshPoints.z.push(coords.z);
                points.push({ row: altStep, col: azStep, idx: points.length });
            }
        }

        // Create triangles for the mesh
        for (let row = 0; row < steps; row++) {
            for (let col = 0; col < steps; col++) {
                const idx = row * (steps + 1) + col;
                const idx_right = idx + 1;
                const idx_bottom = idx + (steps + 1);
                const idx_bottom_right = idx_bottom + 1;

                // First triangle
                meshPoints.i.push(idx);
                meshPoints.j.push(idx_right);
                meshPoints.k.push(idx_bottom);

                // Second triangle
                meshPoints.i.push(idx_right);
                meshPoints.j.push(idx_bottom_right);
                meshPoints.k.push(idx_bottom);
            }
        }

        // Add mesh trace for filled search area
        traces.push({
            type: 'mesh3d',
            x: meshPoints.x,
            y: meshPoints.y,
            z: meshPoints.z,
            i: meshPoints.i,
            j: meshPoints.j,
            k: meshPoints.k,
            opacity: 0.3,
            color: this.darkTheme.searchArea,
            name: 'Search Area',
            showlegend: false,
            hoverinfo: 'skip'
        });

        // Add border outline
        const borderPoints = { x: [], y: [], z: [] };
        const numBorderPoints = 36;

        // Top border
        for (let i = 0; i <= numBorderPoints; i++) {
            const azFrac = i / numBorderPoints;
            const currentAz = azimuth - searchRadius + (2 * searchRadius * azFrac);
            const coords = this.azAltToSphereCoords(currentAz, Math.min(90, maxAltitude), 1.01);
            borderPoints.x.push(coords.x);
            borderPoints.y.push(coords.y);
            borderPoints.z.push(coords.z);
        }

        // Add border trace
        traces.push({
            x: borderPoints.x,
            y: borderPoints.y,
            z: borderPoints.z,
            type: 'scatter3d',
            mode: 'lines',
            line: {
                color: this.darkTheme.accent,
                width: 4
            },
            name: 'Search Border',
            showlegend: true,
            hovertemplate: `Search Area<br>Center: ${azimuth}° Az<br>Alt: ${minAltitude}° - ${maxAltitude}°<br>FOV: ${searchRadius}°<extra></extra>`
        });

        return traces;
    }


    /**
     * Create 3D object path traces
     * @param {Array} objects - Objects to create paths for
     * @returns {Array} Array of path traces
     */
    createObjectPaths3D(objects) {
        const traces = [];

        if (!this.objectPaths) return traces;

        // Create a path trace for each object (limit to top 10 for visibility)
        const topObjects = objects.slice(0, Math.min(objects.length, 15));

        topObjects.forEach((obj, index) => {
            const path = this.objectPaths[obj.name];
            if (!path || !path.x || path.x.length === 0) {
                return;
            }

            // Filter out null values (below horizon)
            const validIndices = path.x.map((x, i) => x !== null ? i : -1).filter(i => i >= 0);
            if (validIndices.length < 2) {
                return; // Need at least 2 points for a path
            }

            const validX = validIndices.map(i => path.x[i]);
            const validY = validIndices.map(i => path.y[i]);
            const validZ = validIndices.map(i => path.z[i]);

            const displayName = obj.displayName || obj.name || obj.designation || 'Object';
            const color = this.getObjectColor(obj, index);
            const dashStyle = obj.object_type?.toLowerCase().includes('nebula') ? 'dot' : 'solid';

            // Create path trace
            traces.push({
                x: validX,
                y: validY,
                z: validZ,
                type: 'scatter3d',
                mode: 'lines',
                line: {
                    color: color,
                    width: 3,
                    dash: dashStyle,
                    opacity: 0.7
                },
                name: displayName,
                legendgroup: displayName,
                showlegend: true,
                hovertemplate: `${obj.name}<br>Path throughout night<extra></extra>`
            });
        });

        return traces;
    }

    /**
     * Convert azimuth/altitude to 3D sphere coordinates
     * @param {number} azimuth - Azimuth in degrees
     * @param {number} altitude - Altitude in degrees
     * @param {number} radius - Sphere radius (default 1)
     * @returns {Object} x,y,z coordinates
     */
    azAltToSphereCoords(azimuth, altitude, radius = 1) {
        // Convert to radians
        const azRad = azimuth * Math.PI / 180;
        const altRad = altitude * Math.PI / 180;

        // Convert to Cartesian coordinates on sphere
        // In astronomical coordinates: azimuth from North, altitude from horizon
        return {
            x: radius * Math.cos(altRad) * Math.sin(azRad),
            y: radius * Math.cos(altRad) * Math.cos(azRad),
            z: radius * Math.sin(altRad) - 0.5
        };
    }

    /**
     * Create 3D celestial sphere using surface (same as main app)
     * @returns {Object} Plotly trace
     */
    createCelestialSphere() {
        // Generate sphere surface data (same approach as main app)
        const steps = 40;
        const sphereX = [];
        const sphereY = [];
        const sphereZ = [];

        for (let i = 0; i <= steps; i++) {
            sphereX[i] = [];
            sphereY[i] = [];
            sphereZ[i] = [];
            for (let j = 0; j <= steps; j++) {
                const theta = (i / steps) * (Math.PI / 2); // 0 to π/2 (hemisphere only - above horizon)
                const phi = (j / steps) * 2 * Math.PI; // 0 to 2π (azimuth)

                // Convert to Cartesian coordinates on unit hemisphere
                sphereX[i][j] = Math.sin(theta) * Math.sin(phi);
                sphereY[i][j] = Math.sin(theta) * Math.cos(phi);
                sphereZ[i][j] = Math.cos(theta) - 0.5;
            }
        }

        return [{
            type: 'surface',
            x: sphereX,
            y: sphereY,
            z: sphereZ,
            opacity: 0.15,
            showscale: false,
            colorscale: [[0, this.darkTheme.sphere], [1, this.darkTheme.sphere]],
            hoverinfo: 'skip',
            showlegend: false
        }];
    }

    /**
     * Create 3D horizon circle
     * @returns {Object} Plotly trace
     */
    createHorizonCircle3D() {
        const points = { x: [], y: [], z: [] };
        const radius = 1;

        // Create horizon circle at altitude = 0°
        for (let az = 0; az <= 360; az += 5) {
            const coords = this.azAltToSphereCoords(az, 0, radius);
            points.x.push(coords.x);
            points.y.push(coords.y);
            points.z.push(coords.z);
        }

        return {
            x: points.x,
            y: points.y,
            z: points.z,
            type: 'scatter3d',
            mode: 'lines',
            line: {
                color: this.darkTheme.horizon,
                width: 6
            },
            name: 'Horizon',
            showlegend: true,
            hoverinfo: 'name'
        };
    }

    /**
     * Create 3D cardinal direction markers (same as main app)
     * @returns {Object} Single Plotly trace with all cardinal points
     */
    createCardinalDirections3D() {
        // Create single trace with all cardinal points (like main app)
        const directions = [
            { label: 'N', az: 0 },
            { label: 'E', az: 90 },
            { label: 'S', az: 180 },
            { label: 'W', az: 270 }
        ];

        const coords = directions.map(dir => this.azAltToSphereCoords(dir.az, 0, 1));

        return {
            type: 'scatter3d',
            x: coords.map(c => c.x),
            y: coords.map(c => c.y),
            z: coords.map(c => c.z),
            mode: 'text',
            text: directions.map(dir => dir.label),
            textposition: 'middle center',
            textfont: { size: 16, color: this.darkTheme.text_primary, family: 'Inter, sans-serif' },
            hoverinfo: 'skip',
            showlegend: false
        };
    }

    /**
     * Get color for object based on type and quality score
     * @param {Object} obj - Object data
     * @returns {string} Color string
     */
    getObjectColor(obj, index = 0) {
        const typeColors = {
            'galaxy': '#FF4757',
            'spiral galaxy': '#FF6B9D',
            'elliptical galaxy': '#C44569',
            'nebula': '#00D2D3',
            'emission nebula': '#54A0FF',
            'reflection nebula': '#48DBF8',
            'planetary nebula': '#0ABDE3',
            'dark nebula': '#341F97',
            'bright nebula': '#5F27CD',
            'star cluster': '#FFC048',
            'globular cluster': '#FF9F43',
            'open cluster': '#FF6B00',
            'double star': '#B2BEC3',
            'star': '#DFE6E9',
            'variable star': '#74B9FF',
            'supernova remnant': '#EE5A24',
            'h ii region': '#A29BFE',
            'wolf-rayet star': '#FD79A8'
        };

        const objectType = obj.object_type?.toLowerCase() || '';
        let baseColor = null;
        for (const [key, color] of Object.entries(typeColors)) {
            if (objectType.includes(key)) {
                baseColor = color;
                break;
            }
        }

        if (baseColor) {
            const rgb = this.hexToRgb(baseColor);
            if (rgb) {
                const variation = (index % 6) * 0.1;
                const lighten = index % 2 === 0;
                const mix = (channel) => {
                    if (lighten) {
                        return Math.round(channel + (255 - channel) * variation);
                    }
                    return Math.round(channel * (1 - variation));
                };
                return `rgb(${mix(rgb.r)}, ${mix(rgb.g)}, ${mix(rgb.b)})`;
            }
            return baseColor;
        }

        return this.getIndexedColor(index);
    }

    /**
     * Generate a distinct color for an index using golden ratio spacing
     * @param {number} index
     * @returns {string} HSL color string
     */
    getIndexedColor(index) {
        const goldenRatio = 0.61803398875;
        const hue = (index * goldenRatio % 1) * 360;
        return `hsl(${Math.round(hue)}, 70%, 55%)`;
    }

    /**
     * Convert a hex color string to RGB components
     * @param {string} hex
     * @returns {{r:number,g:number,b:number}|null}
     */
    hexToRgb(hex) {
        const normalized = hex.startsWith('#') ? hex.slice(1) : hex;
        if (normalized.length !== 6) {
            return null;
        }
        const value = parseInt(normalized, 16);
        if (Number.isNaN(value)) {
            return null;
        }
        return {
            r: (value >> 16) & 255,
            g: (value >> 8) & 255,
            b: value & 255
        };
    }

    /**
     * Create 3D sky chart layout (same as main app)
     * @param {Object} searchParams - Search parameters
     * @returns {Object} Plotly layout
     */
    createSkyLayout(searchParams) {
        const axisConfig = {
            visible: false,
            showaxeslabels: false,
            showticklabels: false,
            showgrid: false,
            zeroline: false,
            range: [-1.1, 1.1]
        };

        const zAxisConfig = {
            ...axisConfig,
            range: [-0.55, 0.6]
        };

        const hasAz = Number.isFinite(searchParams?.azimuth);
        const primaryAlt = Number.isFinite(searchParams?.altitude) ? searchParams.altitude : null;
        const averageAlt = (Number.isFinite(searchParams?.minAltitude) && Number.isFinite(searchParams?.maxAltitude))
            ? (searchParams.maxAltitude + searchParams.minAltitude) / 2
            : null;
        const altValue = Number.isFinite(primaryAlt) ? primaryAlt : averageAlt;

        const suffixParts = [];
        if (hasAz) {
            suffixParts.push(`${Math.round(searchParams.azimuth)}° Az`);
        }
        if (Number.isFinite(altValue)) {
            suffixParts.push(`${Math.round(altValue)}° Alt`);
        }

        const titleText = suffixParts.length > 0
            ? `Sky Direction & Objects — ${suffixParts.join(', ')}`
            : 'Sky Direction & Objects';

        return {
            title: {
                text: titleText,
                font: { size: 18, color: this.darkTheme.text_primary },
                y: 0.98,
                yanchor: 'top',
                x: 0.5,
                xanchor: 'center'
            },
            scene: {
                xaxis: axisConfig,
                yaxis: axisConfig,
                zaxis: zAxisConfig,
                aspectmode: 'manual',
                aspectratio: { x: 1, y: 1, z: 0.5 },
                camera: {
                    eye: { x: 1.2, y: 1.2, z: 1.0 },
                    center: { x: 0, y: 0, z: 0 }
                },
                bgcolor: this.darkTheme.plot_bgcolor
            },
            paper_bgcolor: this.darkTheme.paper_bgcolor,
            plot_bgcolor: this.darkTheme.plot_bgcolor,
            font: { family: 'Inter, sans-serif', color: this.darkTheme.text_secondary },
            margin: { l: 20, r: 20, t: 80, b: 30 },
            autosize: true, // Enable auto-sizing
            showlegend: true,
            legend: {
                orientation: 'h',
                x: 0.5,
                y: 0.92,
                xanchor: 'center',
                yanchor: 'top',
                bgcolor: 'rgba(10, 10, 10, 0.9)',
                bordercolor: this.darkTheme.border,
                borderwidth: 1,
                font: { size: 11, color: this.darkTheme.text_secondary },
                itemsizing: 'constant'
            }
        };
    }

    /**
     * Handle sky chart click events
     * @param {Object} data - Click event data
     */
    handleSkyClick(data) {
        if (data.points && data.points.length > 0) {
            const point = data.points[0];
            console.log('Sky chart click:', point);

            // If clicked on an object, highlight it in results/timeline
            if (point.curveNumber >= 0 && this.currentData) {
                // Find which trace was clicked and handle accordingly
                // This would integrate with the results display and timeline
            }
        }
    }

    /**
     * Show empty sky chart message
     */
    showEmptySkyChart() {
        this.plotDiv.innerHTML = `
            <div class="text-center py-5">
                <i class="fas fa-eye fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No Sky Chart Data</h5>
                <p class="text-muted">Find some objects to see them on the sky map</p>
            </div>
        `;
    }

    /**
     * Calculate square size for 3D plots to prevent egg-shape distortion
     * @param {number} containerWidth - Width available to the plot container
     * @returns {number} Square dimension to use for width/height
     */
    calculateSquareSize(containerWidth) {
        const width = Math.max(0, Math.floor(containerWidth || 0));

        if (width === 0) {
            return 400; // Fallback until DOM has a meaningful width
        }

        const padding = width > 40 ? 20 : 0; // allow some breathing room inside the card
        const size = width - padding;

        // Keep the canvas responsive by following the container width closely,
        // but guarantee a minimum so the scene remains legible on tiny screens.
        return Math.max(240, size);
    }

    /**
     * Clear the sky chart
     */
    clear() {
        if (this.plotDiv) {
            Plotly.purge(this.plotDiv);
        }
        this.currentData = null;
        this.searchParams = null;
        if (this._resizeHandler) {
            window.removeEventListener('resize', this._resizeHandler);
            this._resizeHandler = null;
        }
    }
}

export default SkyVisualization;
